import { useContext } from 'react'
import { EditableWish } from './types'
import { LootContext, PersonalLootContext } from './context'
import Difficulty, { difficulties } from 'app/frontend/types/custom/Difficulty'
import { useTeam } from 'app/frontend/base/config/context/team'
import WishLegacyPriority from 'app/frontend/types/custom/WishLegacyPriority'

export const useFindRelevantWish = (currentSpecId: number) => {
  const { difficulty, configuration } = useContext(LootContext)
  const { changes } = useContext(PersonalLootContext)

  return (item: wowaudit.WishlistItem, isVoidforged = false) => {
    const wishId = `${item.id}-${difficulty}-${currentSpecId}-${configuration.id}`

    return (
      (!isVoidforged && changes[wishId]) ||
      item.wishes.find(
        (wish) =>
          wish.difficulty === difficulties.indexOf(difficulty) &&
          wish.specialization === currentSpecId &&
          configuration.id === wish.configuration_id &&
          !!wish.voidforged === isVoidforged,
      )
    )
  }
}

export const VOIDFORGED_ILVL: Record<number, number> = { 0: 298, 1: 285 }

// Wowhead bonus ids that render the Voidforged version of an item (difficulty
// index 0 = mythic, 1 = heroic).
export const VOIDFORGED_BONUS_ID: Record<number, number> = {
  0: 13654,
  1: 13653,
}

// Matched against the normalized `display_slot`, which folds the various weapon
// item types into one_hand / two_hand / ranged / off_hand.
export const VOIDFORGED_SLOTS = [
  'one_hand',
  'two_hand',
  'ranged',
  'off_hand',
  'trinket',
]

export const wishEmpty = (wish?: EditableWish) => {
  return !wish || wish.type === 'none'
}

export const levelColors = {
  huge: 'bg-destructive-foreground',
  big: 'bg-warning-foreground',
  small: 'bg-success-foreground',
  tiny: 'bg-muted-foreground',
  none: 'bg-transparent',
}

export const defaultWishLabels: Record<WishLegacyPriority, string> = {
  huge: 'Huge',
  big: 'Big',
  small: 'Small',
  tiny: 'Tiny',
  none: 'None',
}

export const useWishLabels = (): Record<WishLegacyPriority, string> => {
  const { team } = useTeam()

  return {
    huge: team.settings.wishlistLabelHuge.value || defaultWishLabels.huge,
    big: team.settings.wishlistLabelBig.value || defaultWishLabels.big,
    small: team.settings.wishlistLabelSmall.value || defaultWishLabels.small,
    tiny: team.settings.wishlistLabelTiny.value || defaultWishLabels.tiny,
    none: defaultWishLabels.none,
  }
}

export const useAllowedWishLabels = (): WishLegacyPriority[] => {
  const { team } = useTeam()
  return team.settings.wishlistAllowedLabels.value as WishLegacyPriority[]
}

export type SeasonWishlistMetadata = {
  tier_priority: { [slot: string]: string }
  trinket_priority: {
    [rank: string]: { item_id: number; obtained: boolean } | null
  }
}

export const seasonWishlistMetadata = (
  metadata: wowaudit.CharacterReference['wishlistMetadata'] | undefined,
  seasonId: number,
): SeasonWishlistMetadata => {
  const seasonData = metadata?.[String(seasonId)]
  return {
    tier_priority: seasonData?.tier_priority ?? {},
    trinket_priority: seasonData?.trinket_priority ?? {},
  }
}

export const withSeasonWishlistMetadata = (
  metadata: wowaudit.CharacterReference['wishlistMetadata'] | undefined,
  seasonId: number,
  patch: Partial<SeasonWishlistMetadata>,
): wowaudit.CharacterReference['wishlistMetadata'] => {
  const seasonKey = String(seasonId)
  return {
    ...metadata,
    [seasonKey]: {
      ...seasonWishlistMetadata(metadata, seasonId),
      ...patch,
    },
  }
}

const DUAL_EQUIP_SLOTS = new Set(['finger', 'trinket'])

const isDroptimizerWish = (wish: wowaudit.Wish) =>
  wish.type === 'raidbots' || wish.type === 'qe'

const findDroptimizerWish = (
  item: wowaudit.WishlistItem,
  specId: number,
  difficultyIndex: number,
  configurationId: number,
) =>
  item.wishes.find(
    (wish) =>
      wish.difficulty === difficultyIndex &&
      wish.specialization === specId &&
      wish.configuration_id === configurationId &&
      !wish.voidforged &&
      isDroptimizerWish(wish),
  )

const droptimizerUpgradeScore = (wish: wowaudit.Wish) =>
  wish.percentage ?? wish.absolute ?? 0

export type EncounterUpgradeItem = {
  item: wowaudit.WishlistItem
  wish?: wowaudit.Wish
}

export type EncounterUpgradeCounts = {
  bis: EncounterUpgradeItem[]
  otherUpgrades: EncounterUpgradeItem[]
  notUpgrades: EncounterUpgradeItem[]
}

const hasDroptimizerWishes = (
  items: wowaudit.WishlistItem[],
  specId: number,
  difficultyIndex: number,
  configurationId: number,
) =>
  items.some(
    (item) =>
      item.specs.includes(specId) &&
      findDroptimizerWish(item, specId, difficultyIndex, configurationId),
  )

// Bonus rolls drop one track higher, so we prefer that difficulty's wishes
// when present. LFR never shows this breakdown.
export const bonusRollWishDifficultyIndex = (
  items: wowaudit.WishlistItem[],
  specId: number,
  difficulty: Difficulty,
  configurationId: number,
): number | null => {
  if (difficulty === 'raid_finder') return null

  const preferredHigher: Partial<Record<Difficulty, Difficulty>> = {
    heroic: 'mythic',
    normal: 'heroic',
  }
  const higher = preferredHigher[difficulty]
  if (higher) {
    const higherIndex = difficulties.indexOf(higher)
    if (hasDroptimizerWishes(items, specId, higherIndex, configurationId)) {
      return higherIndex
    }
  }

  return difficulties.indexOf(difficulty)
}

// Per-encounter droptimizer breakdown. BiS is the biggest upgrade in a slot
// across all sources, or one of the two biggest for rings/trinkets. Returns
// null when this spec/difficulty/config has no droptimizer report.
export const encounterUpgradeCountsBySource = (
  items: wowaudit.WishlistItem[],
  specId: number,
  difficultyIndex: number,
  configurationId: number,
): Record<string, EncounterUpgradeCounts> | null => {
  const specItems = items.filter((item) => item.specs.includes(specId))
  type ScoredItem = {
    item: wowaudit.WishlistItem
    wish?: wowaudit.Wish
    score: number
    isUpgrade: boolean
    hasReport: boolean
  }
  const scored: ScoredItem[] = specItems.map((item) => {
    const wish = findDroptimizerWish(
      item,
      specId,
      difficultyIndex,
      configurationId,
    )
    const score = wish ? droptimizerUpgradeScore(wish) : 0
    return {
      item,
      wish,
      score,
      isUpgrade: !!wish && score > 0,
      hasReport: !!wish,
    }
  })

  if (!scored.some(({ hasReport }) => hasReport)) return null

  const scoresBySlot: Record<string, number[]> = {}
  scored.forEach(({ item, score, isUpgrade }) => {
    if (!isUpgrade) return
    const slot = String(item.display_slot)
    ;(scoresBySlot[slot] ??= []).push(score)
  })

  const bisThresholdBySlot: Record<string, number> = {}
  Object.entries(scoresBySlot).forEach(([slot, scores]) => {
    const sorted = [...scores].sort((a, b) => b - a)
    const limit = DUAL_EQUIP_SLOTS.has(slot) ? 2 : 1
    bisThresholdBySlot[slot] = sorted[Math.min(limit, sorted.length) - 1] ?? 0
  })

  const counts: Record<
    string,
    Record<keyof EncounterUpgradeCounts, ScoredItem[]>
  > = {}
  scored.forEach((entry) => {
    const source = entry.item.source_name
    const current = (counts[source] ??= {
      bis: [],
      otherUpgrades: [],
      notUpgrades: [],
    })

    if (!entry.isUpgrade) {
      current.notUpgrades.push(entry)
      return
    }

    const threshold = bisThresholdBySlot[String(entry.item.display_slot)] ?? 0
    if (entry.score >= threshold) {
      current.bis.push(entry)
    } else {
      current.otherUpgrades.push(entry)
    }
  })

  const byScoreThenName = (a: ScoredItem, b: ScoredItem) =>
    b.score - a.score || a.item.name.localeCompare(b.item.name)

  return Object.fromEntries(
    Object.entries(counts).map(([source, groups]) => [
      source,
      {
        bis: groups.bis.sort(byScoreThenName).map(({ item, wish }) => ({
          item,
          wish,
        })),
        otherUpgrades: groups.otherUpgrades
          .sort(byScoreThenName)
          .map(({ item, wish }) => ({ item, wish })),
        notUpgrades: groups.notUpgrades
          .sort((a, b) => a.item.name.localeCompare(b.item.name))
          .map(({ item, wish }) => ({ item, wish })),
      },
    ]),
  )
}
